// Google One Tap authentication service
// Loads Google Identity Services from CDN and provides One Tap functionality

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleOneTapConfig) => void;
          prompt: (callback?: PromptCallback) => void;
          renderButton: (element: HTMLElement, config: ButtonConfig) => void;
          disableAutoSelect: () => void;
          revoke: (email: string, callback: () => void) => void;
        };
      };
    };
  }
}

interface GoogleOneTapConfig {
  client_id: string;
  callback: (response: { credential: string }) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface PromptCallback {
  (notification: {
    isNotDisplayed: () => boolean;
    isSkippedMoment: () => boolean;
    getDismissedReason: () => string;
  }): void;
}

interface ButtonConfig {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  width?: number;
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

let initialized = false;
let credentialCallback: ((credential: string) => void) | null = null;

export function initializeGoogleOneTap(onCredential: (credential: string) => void): void {
  if (initialized || !window.google) {
    // If not loaded yet, retry after a short delay
    if (!window.google) {
      console.log('Google SDK not loaded yet, waiting...');
      setTimeout(() => initializeGoogleOneTap(onCredential), 100);
      return;
    }
    return;
  }

  if (!GOOGLE_CLIENT_ID) {
    console.warn('VITE_GOOGLE_CLIENT_ID not configured, Google auth disabled');
    return;
  }

  credentialCallback = onCredential;

  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: response => {
      if (response.credential && credentialCallback) {
        credentialCallback(response.credential);
      }
    },
    auto_select: true,
    cancel_on_tap_outside: false,
  });

  initialized = true;
  console.log('Google One Tap initialized');
}

export function promptGoogleOneTap(): void {
  if (!window.google) {
    console.warn('Google SDK not loaded');
    return;
  }
  window.google.accounts.id.prompt(notification => {
    if (notification.isNotDisplayed()) {
      console.log('One Tap not displayed:', notification.getDismissedReason());
    } else if (notification.isSkippedMoment()) {
      console.log('One Tap skipped');
    }
  });
}

export function renderGoogleButton(element: HTMLElement): void {
  if (!window.google) {
    console.warn('Google SDK not loaded');
    return;
  }

  window.google.accounts.id.renderButton(element, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'continue_with',
    width: 280,
  });
}

export function disableAutoSelect(): void {
  window.google?.accounts.id.disableAutoSelect();
}

export function revokeGoogleSession(email: string): Promise<void> {
  return new Promise(resolve => {
    if (window.google) {
      window.google.accounts.id.revoke(email, resolve);
    } else {
      resolve();
    }
  });
}

export function isGoogleAuthConfigured(): boolean {
  return !!GOOGLE_CLIENT_ID;
}
